import { createReducer } from "@reduxjs/toolkit";
import _ from "lodash";
import {
  initPlotQuestion,
  selectOption,
  validateSubmission,
  updateTextAnswer,
} from "./actions";

/**
 * Initial state of the PlotCSCQReducer.
 */
const initialState = {};

/**
 * Reducer for managing plot-related questions in a CSCQ context.
 */
const PlotCSCQReducer = createReducer(initialState, (builder) => {
  /**
   * Handle initialization of a new plot question.
   * Adds a new question to the state if it doesn't already exist.
   */
  builder.addCase(initPlotQuestion, (state, action) => {
    const { key } = action.payload;
    if (!state[key]) {
      state[key] = {
        id: key,
        plot: action.payload.plot,
        plotFallback: action.payload.plotFallback,
        question: _.cloneDeep(action.payload.question), // Clone to avoid potential state mutation issues
        options: action.payload.options,
        hintDetails: action.payload.hintDetails,
        answerId: action.payload.answerId,
        totalAttempts: action.payload.totalAttempts,
        complete: false,
        disabled: false,
        feedback: action.payload.feedback,
        answer: "",
        answerType: action.payload.answerType,
      };
    }
  });

  /**
   * Handle option selection for a specific question.
   * Toggles the selected state of the chosen option and deselects others.
   */
  builder.addCase(selectOption, (state, action) => {
    const { questionId, optionIndex } = action.payload;
    const question = state[questionId];
    if (question && !question.complete) {
      question.options.forEach((option, index) => {
        option.selected = index === optionIndex ? !option.selected : false;
      });
    }
  });

  /**
   * Validate a question's submission and update its state.
   * Checks if the selected answer is correct or reduces attempts for incorrect answers.
   */
  builder.addCase(validateSubmission, (state, action) => {
    const questionId = action.payload;
    const question = state[questionId];

    if (question && !question.complete) {
      if (question.answerType === "SINGLE_SELECT") {
        // Find the selected option
        const selectedOptionIndex = question.options.findIndex(
          (opt) => opt.selected
        );

        if (selectedOptionIndex === -1) return; // No option selected

        const isCorrect = question.answerId.includes(selectedOptionIndex);

        // Update the state of options and question
        question.options.forEach((option, index) => {
          if (index === selectedOptionIndex) {
            option.disabled = true; // Disable only the selected option
            option.selected = false;
          }
        });

        question.complete = isCorrect || question.totalAttempts - 1 === 0; // Complete if correct or out of attempts
        if (!isCorrect) {
          question.totalAttempts -= 1; // Reduce attempts for incorrect answers
        }
      } else if (question.answerType === "OPEN_ENDED") {
        question.complete = true; // Open-ended questions are marked complete on submission
      }
    }
  });

  /**
   * Update the text answer for a question.
   * Used for open-ended questions.
   */
  builder.addCase(updateTextAnswer, (state, action) => {
    const { questionId, value } = action.payload;
    const question = state[questionId];
    if (question) {
      question.answer = value;
    }
  });
});

export default PlotCSCQReducer;
